import CookieManager from "../utilities/cookiemanager";

/**
 * @typedef {object} ReaderSession
 * @property {number=} userId
 * @property {number=} sessionId
 * @property {number=} subscriptionId
 * @property {number=} orderId
 * @property {number=} downloadId
 * @property {number=} cpId
 * @property {boolean=} isUserNotLoggedInButHasContentAccess
 * @property {number=} expires
 */

/**
 * Represents the current reader session. It gets backed up as a cookie to
 * pass information between Kiosk and Reader.
 * The variable is preferred over the cookie to limit abuse via cookie tampering by end users.
 * @type {ReaderSession=}
 */
let readerSession;

function getReaderSession() {
  if (!readerSession) {
    readerSession = getReaderSessionFromCookie();
  }
  return readerSession;
}

/** @returns {ReaderSession=} */
function getReaderSessionFromCookie() {
  const readerSessionString = CookieManager.getCookie(
    CookieManager.cookieKeys.READER_SESSION
  );
  if (readerSessionString) {
    try {
      return JSON.parse(readerSessionString);
    } catch (e) {
      return undefined;
    }
  }
  return undefined;
}

/** @param {ReaderSession} newReaderSession */
export function setReaderSession(newReaderSession, setExpirationDate = true) {
  readerSession = newReaderSession;
  storeReaderSessionAsCookie(newReaderSession, setExpirationDate);
}

/**
 * @param {ReaderSession} readerSession
 * @param {boolean=} setExpirationDate
 */
function storeReaderSessionAsCookie(readerSession, setExpirationDate) {
  let timestamp;
  if (setExpirationDate) {
    timestamp = readerSession.expires;
    if (!timestamp) {
      const date = new Date();
      timestamp = date.setTime(date.getTime() + 1000 * 60 * 60 * 2);
    }
  }
  CookieManager.createCookie(
    CookieManager.cookieKeys.READER_SESSION,
    JSON.stringify({ ...readerSession, expires: timestamp }),
    {
      expires: timestamp,
      path: "/",
      useExpires: setExpirationDate, // treat expires as a timestamp
    }
  );
}

/** @param {Partial<ReaderSession>} readerSessionData */
function updateReaderSession(readerSessionData) {
  const currentReaderSession = getReaderSession();
  setReaderSession({ ...currentReaderSession, ...readerSessionData });
}

export function clearReaderSession() {
  readerSession = undefined;
  CookieManager.removeCookie(CookieManager.cookieKeys.READER_SESSION);
}

/** @param {number | string} contentPackageId */
export function isAlreadyAuthorizedForContentPackage(contentPackageId) {
  const readerSession = getReaderSession();
  if (readerSession && readerSession.cpId) {
    const isSameContentPackage =
      readerSession.cpId.toString() === contentPackageId.toString();
    const isNotExpired = new Date().getTime() <= readerSession.expires;
    return isSameContentPackage && isNotExpired;
  }
  return false;
}

export function isUserNotLoggedInButHasContentAccess() {
  const readerSession = getReaderSession();
  return readerSession && readerSession.isUserNotLoggedInButHasContentAccess
    ? readerSession.isUserNotLoggedInButHasContentAccess
    : false;
}

/** @param {boolean} hasAccess */
export function setUserNotLoggedInButHasContentAccess(hasAccess) {
  updateReaderSession({
    isUserNotLoggedInButHasContentAccess: hasAccess,
  });
}

export function getDownloadId() {
  const readerSession = getReaderSession();
  return readerSession ? readerSession.downloadId : undefined;
}

export function getOrderId() {
  const readerSession = getReaderSession();
  return readerSession ? readerSession.orderId : undefined;
}



// WEBPACK FOOTER //
// ./src/common/services/readerSession.js